import type { RegistroOdontograma } from '../api/tipos';

/**
 * Modelo de presentación del odontograma. La notación canónica es FDI/ISO, igual que en el
 * servidor; la numeración Universal existe sólo como proyección para el profesional
 * formado con ella, y nunca se persiste.
 */

export type Superficie = 'V' | 'L' | 'M' | 'D' | 'O';

export const SUPERFICIES: { codigo: Superficie; nombre: string }[] = [
  { codigo: 'V', nombre: 'Vestibular' },
  { codigo: 'L', nombre: 'Lingual / palatina' },
  { codigo: 'M', nombre: 'Mesial' },
  { codigo: 'D', nombre: 'Distal' },
  { codigo: 'O', nombre: 'Oclusal / incisal' },
];

/** Cuadrantes FDI de la dentición permanente, en el orden en que se dibujan. */
const PERMANENTES = {
  supDerecha: ['18', '17', '16', '15', '14', '13', '12', '11'],
  supIzquierda: ['21', '22', '23', '24', '25', '26', '27', '28'],
  infIzquierda: ['31', '32', '33', '34', '35', '36', '37', '38'],
  infDerecha: ['48', '47', '46', '45', '44', '43', '42', '41'],
};

/** Dentición temporaria: cinco piezas por cuadrante, con la misma lógica de dibujo. */
const TEMPORARIAS = {
  supDerecha: ['55', '54', '53', '52', '51'],
  supIzquierda: ['61', '62', '63', '64', '65'],
  infIzquierda: ['71', '72', '73', '74', '75'],
  infDerecha: ['85', '84', '83', '82', '81'],
};

export type Denticion = 'Permanente' | 'Temporaria' | 'Mixta';

/** Filas de piezas tal como se ven en la boca: arriba de derecha a izquierda, y abajo. */
export function filasDe(denticion: Denticion): { superior: string[]; inferior: string[] }[] {
  const permanente = {
    superior: [...PERMANENTES.supDerecha, ...PERMANENTES.supIzquierda],
    inferior: [...PERMANENTES.infDerecha, ...PERMANENTES.infIzquierda],
  };

  const temporaria = {
    superior: [...TEMPORARIAS.supDerecha, ...TEMPORARIAS.supIzquierda],
    inferior: [...TEMPORARIAS.infDerecha, ...TEMPORARIAS.infIzquierda],
  };

  if (denticion === 'Permanente') {
    return [permanente];
  }

  if (denticion === 'Temporaria') {
    return [temporaria];
  }

  // En dentición mixta se ven las dos, porque el paciente en recambio tiene las dos.
  return [permanente, temporaria];
}

/**
 * Estados que la interfaz sabe pintar. El servidor acepta cualquier código del catálogo;
 * el que no esté acá se dibuja con el color neutro y su nombre tal cual, así un catálogo
 * ampliado no rompe la pantalla.
 */
export const ESTADOS_SUPERFICIE: Record<string, { nombre: string; color: string; borde: string }> = {
  sano: { nombre: 'Sano', color: 'transparent', borde: '#d7d2c5' },
  caries: { nombre: 'Caries', color: '#c94a32', borde: '#9b3421' },
  restauracion: { nombre: 'Restauración', color: '#1d6557', borde: '#134339' },
  sellante: { nombre: 'Sellante', color: '#469c88', borde: '#175248' },
  fractura: { nombre: 'Fractura', color: '#b8862a', borde: '#8c6416' },
  desgaste: { nombre: 'Desgaste', color: '#d7d2c5', borde: '#7a8691' },
  provisorio: { nombre: 'Provisorio', color: '#a8d7ca', borde: '#2b7f6d' },
};

export const ESTADOS_PIEZA: Record<string, { nombre: string; color: string; marca: string }> = {
  ausente: { nombre: 'Ausente', color: '#e9e6dd', marca: '✕' },
  extraccion_indicada: { nombre: 'Extracción indicada', color: '#fdf1ef', marca: '✕' },
  implante: { nombre: 'Implante', color: '#d3ebe4', marca: 'I' },
  corona: { nombre: 'Corona', color: '#a8d7ca', marca: 'C' },
  puente: { nombre: 'Puente', color: '#a8d7ca', marca: 'P' },
  protesis_removible: { nombre: 'Prótesis removible', color: '#d3ebe4', marca: 'R' },
  endodoncia: { nombre: 'Endodoncia', color: '#f2ddab', marca: 'E' },
  resto_radicular: { nombre: 'Resto radicular', color: '#f6c6bd', marca: 'r' },
  supernumerario: { nombre: 'Supernumerario', color: '#d3ebe4', marca: 'S' },
  en_erupcion: { nombre: 'En erupción', color: '#f5f3ee', marca: '↑' },
  retenido: { nombre: 'Retenido', color: '#e9e6dd', marca: '↓' },
};

export function nombreDeEstado(estado: string): string {
  return (
    ESTADOS_SUPERFICIE[estado]?.nombre ??
    ESTADOS_PIEZA[estado]?.nombre ??
    estado.replace(/_/g, ' ')
  );
}

/** Índice por pieza para dibujar sin recorrer la lista completa en cada diente. */
export type EstadoDePieza = {
  superficies: Partial<Record<Superficie, string>>;
  pieza: string | null;
  notas: string[];
};

export type MapaOdontograma = Map<string, { hallazgo: EstadoDePieza; plan: EstadoDePieza }>;

function vacio(): EstadoDePieza {
  return { superficies: {}, pieza: null, notas: [] };
}

export function indexar(registros: RegistroOdontograma[]): MapaOdontograma {
  const mapa: MapaOdontograma = new Map();

  for (const registro of registros) {
    let entrada = mapa.get(registro.pieza);

    if (!entrada) {
      entrada = { hallazgo: vacio(), plan: vacio() };
      mapa.set(registro.pieza, entrada);
    }

    const capa = registro.capa === 'Plan' ? entrada.plan : entrada.hallazgo;

    if (registro.superficie) {
      capa.superficies[registro.superficie as Superficie] = registro.estado;
    } else {
      capa.pieza = registro.estado;
    }

    if (registro.nota) {
      capa.notas.push(registro.nota);
    }
  }

  return mapa;
}

/** Vuelve del índice a la lista que espera la API. */
export function aRegistros(mapa: MapaOdontograma): RegistroOdontograma[] {
  const registros: RegistroOdontograma[] = [];

  for (const [pieza, entrada] of mapa) {
    for (const [nombreCapa, capa] of [
      ['Hallazgo', entrada.hallazgo],
      ['Plan', entrada.plan],
    ] as const) {
      if (capa.pieza) {
        registros.push({
          pieza,
          capa: nombreCapa,
          ambito: 'Pieza',
          superficie: null,
          estado: capa.pieza,
          nota: capa.notas[0] ?? null,
        });
      }

      for (const [superficie, estado] of Object.entries(capa.superficies)) {
        if (estado) {
          registros.push({
            pieza,
            capa: nombreCapa,
            ambito: 'Superficie',
            superficie,
            estado,
            nota: null,
          });
        }
      }
    }
  }

  return registros;
}

/**
 * Proyección a numeración Universal (1–32), sólo para mostrar. La tabla se deriva del
 * recorrido FDI: 18→1 … 28→16, 38→17 … 48→32.
 */
export function aUniversal(fdi: string): string {
  const orden = [
    ...PERMANENTES.supDerecha,
    ...PERMANENTES.supIzquierda,
    ...PERMANENTES.infIzquierda.slice().reverse(),
    ...PERMANENTES.infDerecha.slice().reverse(),
  ];

  const indice = orden.indexOf(fdi);

  if (indice >= 0) {
    return `${indice + 1}`;
  }

  // Las temporarias se numeran con letras A–T en el sistema Universal.
  const temporarias = [
    ...TEMPORARIAS.supDerecha,
    ...TEMPORARIAS.supIzquierda,
    ...TEMPORARIAS.infIzquierda.slice().reverse(),
    ...TEMPORARIAS.infDerecha.slice().reverse(),
  ];

  const letra = temporarias.indexOf(fdi);

  return letra >= 0 ? String.fromCharCode(65 + letra) : fdi;
}
